import { useState } from "react";
import { ChevronDown, Sparkles } from "lucide-react";

const WD = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
const MO = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
const day0 = (iso) => new Date(`${iso.slice(0, 10)}T00:00:00`);
export const daysTo = (iso) => Math.round((day0(iso) - new Date().setHours(0, 0, 0, 0)) / 864e5);
const sameWeek = (iso) => { const t = new Date(); t.setHours(0, 0, 0, 0); const mon = new Date(t); mon.setDate(t.getDate() - ((t.getDay() + 6) % 7)); const d = day0(iso); return d >= mon && d - mon < 7 * 864e5; };

export function groupTodo(todo) {
  const by = new Map();
  todo.forEach((r) => {
    if (!by.has(r.pid)) by.set(r.pid, { ...r, items: [] });
    by.get(r.pid).items.push(r);
  });
  return [...by.values()].map((g) => {
    g.items.sort((a, b) => !!b.hl - !!a.hl);
    const n = g.date != null ? daysTo(g.date) : null;
    const risk = n != null && n >= 0 && n <= 7;
    const nv = g.items.find((r) => r.novo);
    return { ...g, n, risk, novo: !!nv, novo_tag: nv?.novo_tag, tone: risk ? "red" : "amber" };
  }).sort((a, b) => (b.novo - a.novo) || (a.date || "9999").localeCompare(b.date || "9999"));
}

function state(g) {
  if (g.n === 0) return ["Hoje", "bg-red-50 text-red-700"];
  if (g.n === 1) return ["Amanhã", "bg-amber-50 text-amber-800"];
  if (g.risk) return [`Em risco · ${g.n} ${g.n === 1 ? "dia" : "dias"}`, "bg-red-50 text-red-700"];
  const left = g.faltam ?? (g.total ? g.total - g.done : 0);
  if (left > 0) return [left === 1 ? "Falta 1" : `Faltam ${left}`, "bg-amber-50 text-amber-800"];
  if (g.corrigir > 0) return [`${g.corrigir} a corrigir`, "bg-amber-50 text-amber-800"];
  if (g.rever > 0) return [`${g.rever} por rever`, "bg-sky-50 text-sky-800"];
  return g.pend === 1 ? ["1 pendente", "bg-amber-50 text-amber-800"] : [`${g.pend ?? g.items.length} pendentes`, "bg-amber-50 text-amber-800"];
}

function DateCol({ g }) {
  if (!g.date) return <div className="w-[88px] shrink-0 border-r border-slate-100" />;
  const d = day0(g.date);
  return (
    <div className="w-[76px] sm:w-[92px] shrink-0 border-r border-slate-100 grid place-items-center content-center gap-0.5 py-4" data-testid={`gp-card-date-${g.pid}`}>
      <span className="text-xs font-bold text-slate-500">{WD[d.getDay()]}</span>
      <span className="text-3xl font-bold navy-text leading-none">{d.getDate()}</span>
      {!sameWeek(g.date) && <span className="text-xs font-bold text-slate-500">{MO[d.getMonth()]}</span>}
      <span className={`mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${g.kind?.includes("CPCV") ? "bg-violet-50 text-violet-700" : "bg-slate-900 text-white"}`}>{g.kind}</span>
    </div>
  );
}

export function ProcessCard({ g, onOpen }) {
  const [all, setAll] = useState(false);
  const [lbl, cls] = state(g);
  const [main, ...rest] = g.items;
  const pct = g.total ? Math.round((g.done / g.total) * 100) : 0;
  const bar = g.risk ? "bg-red-600" : g.items.length ? "bg-orange-500" : "bg-emerald-500";
  return (
    <div className={`bg-white rounded-2xl border shadow-[0_4px_20px_rgb(0,0,0,0.04)] flex ${g.novo ? "border-emerald-300 ring-2 ring-emerald-200" : "border-slate-100"}`} data-testid={`gp-group-${g.pid}`}>
      <DateCol g={g} />
      <div className="flex-1 min-w-0 p-4 sm:p-5 space-y-3">
        {g.novo && <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700" data-testid={`gp-group-novo-${g.pid}`}><Sparkles size={13} /> {g.novo_tag || "Novo processo"}</span>}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0"><span className="text-[17px] font-semibold text-slate-900" data-testid={`gp-group-address-${g.pid}`}>{g.short}</span>
            {g.ensaio && <span className="ml-2 align-middle text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700" data-testid={`gp-group-ensaio-${g.pid}`}>Ensaio</span>}
            <span className="text-sm text-slate-500"> {[g.typ, g.consultant].filter(Boolean).join(" · ")}</span></div>
          <span className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full ${cls}`} data-testid={`gp-group-count-${g.pid}`}>{lbl}</span>
        </div>
        {g.total > 0 && <div className="flex items-center gap-3"><div className="h-1.5 w-28 sm:w-64 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${bar}`} style={{ width: `${pct}%` }} /></div>
          <span className="text-sm text-slate-500 whitespace-nowrap" data-testid={`gp-group-progress-${g.pid}`}>{g.docs_line || `${g.done} de ${g.total} prontos`}</span></div>}
        <div className="rounded-xl bg-slate-50 px-4 py-3 flex flex-wrap sm:flex-nowrap items-center gap-3" data-testid={`gp-todo-${main.id}`}>
          <div className="flex-1 min-w-0"><div className="text-[15px] font-semibold text-slate-900">{main.title}</div>{main.context && <div className="text-sm text-slate-500">{main.context}</div>}</div>
          <button onClick={() => onOpen(main)} className="w-full sm:w-auto h-10 px-5 rounded-xl navy-bg text-white text-sm font-semibold hover:brightness-110 transition-[filter]" data-testid={`gp-todo-btn-${main.id}`}>{main.label || "Abrir"}</button>
        </div>
        {rest.length > 0 && (
          <div className="text-sm text-slate-600 space-y-1.5">
            {(all ? rest : rest.slice(0, 1)).map((r) => (
              <div key={r.id} className="flex items-center gap-2" data-testid={`gp-todo-${r.id}`}><span className="w-1.5 h-1.5 rounded-full bg-slate-500" /><span>{r.title}</span><button onClick={() => onOpen(r)} className="ml-1 shrink-0 px-2 py-0.5 rounded-md border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-white" data-testid={`gp-todo-btn-${r.id}`}>{r.label || "Abrir"}</button></div>
            ))}
            {rest.length > 1 && <button onClick={() => setAll(!all)} className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900" data-testid={`gp-group-more-${g.pid}`}>{all ? "Mostrar menos" : `+ ${rest.length - 1} ${rest.length - 1 === 1 ? "tarefa tua" : "tarefas tuas"}`}<ChevronDown size={14} className={all ? "rotate-180" : ""} /></button>}
          </div>
        )}
      </div>
    </div>
  );
}

const nudged = (r) => r.nudged_at && (!r.last_sent || r.nudged_at.slice(0, 10) >= r.last_sent);

export function WaitList({ rows, onRemind, onNudge }) {
  if (!rows.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100 px-4" data-testid="gp-waiting">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-3 py-3" data-testid={`gp-wait-${r.id}`}>
          <span className="w-8 h-8 shrink-0 grid place-items-center rounded-lg bg-slate-100 text-sm font-bold text-slate-600">{r.seller ? "V" : "C"}</span>
          <div className="flex-1 min-w-0"><div className="text-[15px] text-slate-800 truncate">{r.title}</div>
            <div className="text-sm text-slate-500 truncate">{[r.short, r.when, r.note].filter(Boolean).join(" · ")}</div></div>
          {r.tag ? (nudged(r) ? <span className="shrink-0 text-sm font-semibold text-slate-500" data-testid={`gp-wait-sent-${r.id}`}>{r.consultant_f ? "Consultora avisada" : "Consultor avisado"}</span>
            : <button onClick={() => onNudge(r)} className="shrink-0 text-sm font-semibold text-red-700 hover:underline" data-testid={`gp-wait-nudge-${r.id}`}>{r.consultant_f ? "Avisar consultora" : "Avisar consultor"}</button>)
            : r.remind ? <button onClick={() => onRemind(r)} className="shrink-0 text-sm font-semibold navy-text hover:underline" data-testid={`gp-wait-remind-${r.id}`}>Lembrar de novo</button>
              : <span className="shrink-0 text-sm text-slate-500" data-testid={`gp-wait-client-${r.id}`}>À espera</span>}
        </div>
      ))}
    </div>
  );
}
